/**
 * Hero variants are pre-generated at build time by
 * `scripts/generate-hero-variants.mjs` — for each `<stem>.webp`
 * source, we emit `<stem>-480.webp`, `<stem>-768.webp`, and
 * `<stem>-1024.webp`. PageHero serves a `<picture>` srcset pointing
 * at those static files directly from /asset/ (Cloudflare edge
 * cache, immutable, ~year-long max-age) — no detour through
 * `/_next/image?...`, which is a passthrough on Workers anyway
 * (sharp doesn't run there) and just adds a worker hop to ship the
 * exact same bytes.
 *
 * Net effect: a phone at 480 CSS-px gets ~25 KB; the previous setup
 * shipped the source 1376px file (~120 KB) regardless of viewport.
 */
const HERO_VARIANT_WIDTHS = [480, 768, 1024] as const;

function buildHeroImageSrcSet(src: string): string {
  // src is `/asset/<stem>.webp` — derive `<stem>` and emit the
  // pre-generated variant URLs. The original (largest) source is the
  // fallback for ≥1024 viewports.
  const m = src.match(/^(\/asset\/[A-Za-z0-9._-]+?)(\.webp|\.avif|\.jpg|\.jpeg|\.png)$/i);
  if (!m) return "";
  const stem = m[1];
  const ext = m[2];
  const variants = HERO_VARIANT_WIDTHS.map((w) => `${stem}-${w}${ext} ${w}w`);
  // Source is the de-facto largest (typically 1376w but occasionally
  // 1000-1320). Quote it as the largest descriptor so the browser
  // picks it on widescreen.
  variants.push(`${src} 1920w`);
  return variants.join(", ");
}

export interface PageHeroProps {
  src: string;
  alt: string;
  variant: "with-cta" | "image-only";
  ariaLabel: string;
  /** Overline text above the title (e.g. "About Us", "Help Centre") */
  subtitle?: string;
  /** Main h1 heading */
  title?: string;
  /** Italic description below the title */
  description?: string;
  /** Extra content below the standard subtitle/title/description (e.g. buttons) */
  children?: React.ReactNode;
  /**
   * CSS `object-position` for the hero image (e.g. "50% 30%"). Heroes crop to
   * `object-cover`; set this so the subject (a face, a bouquet sitting low/left
   * in frame) stays in view instead of being cropped out at the default center.
   */
  objectPosition?: string;
  /**
   * Drops the sm+ `h-[50vh]` / `max-h-150` ceiling so the hero
   * grows to fit content. Set on pages that stack many content rows
   * inside the hero (the homepage stacks 7 — subtitle, h1, caveat
   * tagline, description, 2 CTAs, delivery note, workshops link)
   * and otherwise gets visibly squashed at sm/md viewports where the
   * 600px cap is shorter than the natural content height. Default
   * `false` keeps the consistent layout that every other page uses.
   */
  flexHeight?: boolean;
}

/**
 * Consistent hero section across all pages.
 * - with-cta: image + overlay + centered content
 * - image-only: decorative image only
 *
 * Server component — no client JS, no fade-in state. The previous
 * `useState(loaded)` + opacity-0 + onLoad fade-in pattern delayed the
 * hero paint until hydration ran AND the image decoded AND the 700ms
 * transition finished. That suppressed `fetchPriority="high"` on the
 * rendered `<img>` and made the LCP element wait for client JS — the
 * worst of both CWV worlds. Letting `next/image` render natively
 * preserves the priority hint and paints as soon as the image decodes.
 */
export function PageHero({
  src,
  alt,
  variant,
  ariaLabel,
  subtitle,
  title,
  description,
  children,
  flexHeight = false,
  objectPosition,
}: PageHeroProps) {
  // Sizing is a grow-to-fit MINIMUM, never a hard height — so hero content
  // can never be clipped (the old `h-[50vh] max-h-150` cap + `justify-
  // center` clipped content-heavy heroes like /faq, whose search box + chip
  // row overflowed the 600px box: the top slid behind the fixed header and
  // the chips were cut off).
  //
  // Default: `min-h-[clamp(360px,50vh,600px)]` reproduces the previous height
  // for the common case (content shorter than the floor renders at exactly the
  // same clamped height, so every simple hero still reads uniform) BUT grows
  // when content is taller, keeping the y-padding intact. `flexHeight` just
  // uses a lower floor for pages that are always tall (the homepage's 7 rows),
  // so they don't carry excess whitespace above the fold.
  const sizingClasses = flexHeight
    ? "min-h-70 sm:min-h-105"
    : "min-h-70 sm:min-h-[clamp(360px,50vh,600px)]";
  // Build the responsive `srcSet`/`sizes` for the rendered `<img>` below.
  //
  // LIVE-1 (2026-07-06): this component used to ALSO mirror the srcSet onto a
  // `<link rel="preload" as="image">` in the hoisted <head>. That preload was
  // the source of a cross-route leak: React 19 dispatches the preload HINT for
  // every server-rendered `<link rel="preload">` as it parses a route's Flight
  // stream — including when Next's App Router *prefetches* a linked route, or
  // when the SPA navigates away and React never GCs the hoisted tag. So the
  // home page (which links to /events and /gallery) ended up with the /events
  // hero (`event-hero-*.webp`) and the /gallery LCP image preloaded into ITS
  // <head>, where they went unused ("preloaded but not used within a few
  // seconds"). Worse, the tag carried `imageSrcSet` with NO `href`, so React
  // couldn't dedupe it and it appeared multiple times (the observed ×3).
  //
  // The fix: DON'T emit a `<link rel="preload">` from this reusable component.
  // The hero renders a plain `<img src srcSet sizes fetchPriority="high">` in
  // the initial SSR body (see below), which the browser's preload scanner
  // discovers during HTML parse and fetches immediately at high priority — the
  // same LCP head-start a preload gives, but scoped to the page that actually
  // renders it, so nothing leaks onto sibling routes via prefetch/navigation.
  //
  // LIVE-3 (2026-08-03) — ⚠ THAT FIX WAS ONLY HALF THE STORY, AND THE LEAK WAS
  // STILL LIVE A MONTH LATER. Deleting our own tag changed nothing, because
  // **React 19 synthesises the identical tag from the `<img>` itself.** Both
  // renderers do it, unprompted:
  //
  //   • HTML SSR (`react-dom-server`, `case "img":`) pushes
  //     `{rel:"preload", as:"image", href: srcSet ? undefined : src,
  //       imageSrcSet: srcSet, imageSizes: sizes, fetchPriority}` into
  //     `renderState.highImagePreloads` — note `href` is UNDEFINED whenever a
  //     `srcSet` exists, which is precisely why the observed tag had no href
  //     and could not be deduped.
  //   • The RSC/Flight serializer (`react-server-dom-*-server`, same `case
  //     "img":`) calls `preload(src, "image", {imageSrcSet, imageSizes,
  //     fetchPriority})`, which ships as a Flight HINT row. The client
  //     dispatches hint rows **as it decodes the stream** — so merely
  //     PREFETCHING a route injects that route's hero preload into whatever
  //     document you are currently on.
  //
  // Measured on production `/` (2026-08-03): the home page prefetches /events
  // (hero CTA + header nav), so `/asset/event-hero.webp` was fetched at
  // 154,178 B — the exact byte size of the un-resized original, because
  // `imagesizes="100vw"` selects our `1920w` candidate — and rendered by
  // nothing. `document.querySelectorAll("img")` filtered for it returned [].
  //
  // Both renderers skip the synthetic preload under the SAME four conditions:
  // `loading="lazy"`, `fetchPriority="low"`, no src/srcSet, or the `<img>`
  // being inside `<picture>`/`<noscript>` (`tagScope & 3` / `parentContext & 3`;
  // `case "picture"` sets that bit). The first three all destroy the LCP hint we
  // want, so the hero `<img>` is wrapped in a `<picture>` below. That keeps the
  // scanner-discoverable `<img src srcSet sizes fetchPriority="high">` in the
  // SSR body — the mechanism this component was always documented to rely on —
  // while stopping the head tag that leaks across prefetch boundaries.
  //
  // `<picture>` is `display: inline` and NOT positioned, so the hero `<img>`'s
  // `absolute inset-0` still resolves against the wrapper div. Layout, sizing
  // and object-fit are byte-identical; only the synthetic <head> tag goes away.
  const heroImageSrcSet = buildHeroImageSrcSet(src);
  const heroImageSizes = "100vw";
  return (
    <section
      // Negative top margin pulls the hero UP behind the fixed Header so
      // there's no visible gap between the header and the hero image. The
      // header reserves `4rem/5rem/5.5rem + var(--banner-h)` on `<main>`'s
      // padding-top, and we mirror that here so the hero edge meets the top
      // of the viewport. `var(--banner-h)` is set live by AnnouncementBar
      // (see components/layout/AnnouncementBar.tsx), so the negative pull
      // grows when the banner is visible and shrinks back to 0 on dismiss.
      //
      // Inner padding (pt) stays oversized so hero content (subtitle/title/
      // buttons) sits well below where the header+banner overlay, even on
      // mobile where the banner can wrap to two lines.
      className={`relative -mt-[calc(4rem+var(--banner-h,0px))] flex flex-col items-center justify-center px-6 pb-8 pt-[calc(5rem+var(--banner-h,0px))] sm:pb-12 sm:pt-[calc(7rem+var(--banner-h,0px))] md:-mt-[calc(5rem+var(--banner-h,0px))] md:px-10 md:pb-14 md:pt-[calc(8rem+var(--banner-h,0px))] lg:-mt-[calc(5.5rem+var(--banner-h,0px))] lg:px-16 lg:pb-16 lg:pt-[calc(9rem+var(--banner-h,0px))] ${sizingClasses}`}
      {...(variant === "with-cta" && title
        ? { "aria-labelledby": "page-hero-title" }
        : { "aria-label": ariaLabel })}
    >
      {/* NO `<link rel="preload">` here — see the LIVE-1/LIVE-3 note above. The
          LCP hint lives on the <img fetchPriority="high"> below, which the
          preload scanner picks up during HTML parse without leaking onto routes
          that merely link here. */}
      <div className="absolute inset-0">
        {/* Plain <img> rather than next/image: on Workers `sharp`
            doesn't run, so /_next/image is a passthrough anyway —
            it just adds a worker hop to ship the exact same bytes.
            Serving the pre-resized variants directly from /asset/
            cuts ~50ms off the LCP path on cold caches.

            LIVE-3: the <picture> wrapper is LOAD-BEARING, not decoration. It is
            the only one of React 19's four opt-outs from the synthetic
            `<link rel=preload as=image>` that does not also throw away the LCP
            hint (`loading="lazy"`, `fetchPriority="low"` and dropping srcSet all
            do). Unwrap it and every page that merely LINKS to a PageHero route
            starts downloading that route's hero on prefetch. Do not add
            `<source>` children to "make it a real picture" — the srcSet on the
            <img> already selects the pre-generated variants. */}
        <picture>
          {/* No eslint-disable needed: @next/next/no-img-element does not fire
              for an <img> inside a <picture>. That is load-bearing — unwrap the
              <picture> and lint fails too, not just PRELOADLEAK-1. */}
          <img
            src={src}
            srcSet={heroImageSrcSet}
            sizes={heroImageSizes}
            alt={alt}
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover object-center"
            // Focal override: keeps the subject in frame when the hero crops.
            // Falls back to the class's object-center when unset.
            style={objectPosition ? { objectPosition } : undefined}
            // No loading="lazy" — this is the LCP element on every page. (It
            // would ALSO suppress the synthetic preload, but at the cost of the
            // thing we actually want: an eager, high-priority LCP fetch.)
          />
        </picture>
      </div>
      {variant === "with-cta" && (
        <>
          <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
          <div className="hero-cta-content relative z-10 mx-auto w-full max-w-7xl text-center text-white">
            {subtitle && (
              <p className="font-serif text-sm font-medium uppercase tracking-[0.15em] text-white sm:text-base">
                {subtitle}
              </p>
            )}
            {title && <h1 id="page-hero-title" className="mt-4 heading-hero xl:text-6xl">{title}</h1>}
            {description && (
              <p className="mt-3 font-serif text-lg italic text-white sm:text-xl">
                {description}
              </p>
            )}
            {children}
          </div>
        </>
      )}
    </section>
  );
}
